import { useTranslation } from "react-i18next";
import React from "react";
import SETTINGS_CONTEXT from "src/context/internal/settings";
import { TableHeader } from "src/components/base/table";
import { Strong, Text } from "src/components/base/text";
import { Badge } from "src/components/base/badge";
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownHeading,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
    DropdownSection,
} from "src/components/base/dropdown";
import { notify } from "src/components/base/toast";
import {
    ChevronDownIcon,
    ClipboardDocumentIcon,
    ClockIcon,
    EllipsisVerticalIcon,
    PencilSquareIcon,
    TrashIcon,
} from "@heroicons/react/20/solid";
import { FullDevice } from "src/api/generated/frontend_internal_v1";
import { TagBadge } from "src/components/badges/tag";
import { DeviceGroupBadge } from "src/components/badges/device-group";
import { TenantBadge } from "src/components/badges/tenant";
import { StatusBadge } from "src/components/badges/status";
import { HighlightTerm } from "src/components/highlight-term";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";

/**
 * Format an absolute date using the user's locale (falls back to en-DE).
 *
 * @param value
 * @returns the formatted date, or "—" if `value` is empty/invalid
 */
function formatDate(value: string | null | undefined): string {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("de-de", { year: "numeric", month: "short", day: "2-digit" });
}

/**
 * Parse a dotted-decimal IPv4 address into its 32-bit unsigned integer value.
 *
 * @param value
 * @returns the numeric value, or `null` if `value` is missing or not a valid IPv4 address
 */
function ipv4ToInt(value: string | null | undefined): number | null {
    if (!value) return null;
    const octets = value.split(".").map(Number);
    if (octets.length !== 4 || octets.some((octet) => !Number.isInteger(octet) || octet < 0 || octet > 255)) {
        return null;
    }
    return octets.reduce((acc, octet) => acc * 256 + octet, 0);
}

/**
 * The properties for {@link useDeviceColumns}
 */
export type DeviceColumnsProps = {
    /** Open the rename device popup */
    setOpenRenameDevice: (device: FullDevice) => void;

    /** Open the expire-key popup */
    setOpenExpireKey: (device: FullDevice) => void;

    /** Open the delete device popup */
    setOpenDeleteDevice: (device: FullDevice) => void;
};

/**
 * The columns in the device overview table
 */
export function useDeviceColumns(props: DeviceColumnsProps): ColumnDef<FullDevice, string>[] {
    const [t] = useTranslation("admin-device-overview");
    const [tg] = useTranslation();

    const { setOpenRenameDevice, setOpenDeleteDevice, setOpenExpireKey } = props;

    const columnHelper = createColumnHelper<FullDevice>();
    const settingsContext = React.useContext(SETTINGS_CONTEXT);

    return [
        columnHelper.accessor((device) => device.tenant.name, {
            id: "tenant",
            filterFn: (row, _columnId, filterValue: string) => row.original.tenant.uuid === filterValue,
            header: ({ column }) => (
                <TableHeader
                    sortable
                    direction={column.getIsSorted() || undefined}
                    onSort={column.getToggleSortingHandler()}
                >
                    {t("label.tenant")}
                </TableHeader>
            ),
            cell: (info) => <TenantBadge tenant={info.row.original.tenant} />,
        }),

        columnHelper.accessor(
            (device) => {
                const userPart = device.user?.display_name ?? "";
                const tagPart = device.tags?.map((t) => t.name).join(" ") ?? "";
                const groupPart = device.device_groups?.map((g) => g.name).join(" ") ?? "";
                return `${device.dnsname} ${userPart} ${tagPart} ${groupPart}`;
            },
            {
                id: "device-name",
                filterFn: (row, _columnId, filterValue: boolean) =>
                    filterValue ? row.original.user === null : row.original.user !== null,
                header: ({ column }) => (
                    <TableHeader
                        sortable
                        direction={column.getIsSorted() || undefined}
                        onSort={column.getToggleSortingHandler()}
                    >
                        {t("label.device-name")}
                    </TableHeader>
                ),
                cell: (info) => {
                    const device = info.row.original;
                    return (
                        <div className={"flex flex-col gap-1"}>
                            <Strong>
                                <HighlightTerm value={device.dnsname} />
                            </Strong>
                            {device.user ? (
                                <div className={"flex flex-col gap-1"}>
                                    <Text className={"text-xs"}>
                                        <HighlightTerm value={device.user.display_name} />
                                    </Text>
                                    {device.device_groups && device.device_groups.length > 0 && (
                                        <div className={"flex flex-wrap gap-1"}>
                                            {device.device_groups.map((deviceGroup) => (
                                                <DeviceGroupBadge key={deviceGroup.uuid} deviceGroup={deviceGroup} />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                device.tags &&
                                device.tags.length > 0 && (
                                    <div className={"flex flex-wrap gap-1"}>
                                        {device.tags.map((tag) => (
                                            <TagBadge key={tag.uuid} tag={tag} />
                                        ))}
                                    </div>
                                )
                            )}
                        </div>
                    );
                },
            },
        ),

        columnHelper.accessor((device) => device.last_seen ?? "", {
            id: "last-seen",
            enableGlobalFilter: false,
            filterFn: (row, _columnId, filterValue: boolean) => (filterValue ? row.original.online : true),
            sortingFn: (rowA, rowB) => {
                const a = rowA.original;
                const b = rowB.original;
                // sort online devices before all offline devices
                if (a.online !== b.online) return a.online ? 1 : -1;
                return (a.last_seen ?? "").localeCompare(b.last_seen ?? "");
            },
            header: ({ column }) => (
                <TableHeader
                    sortable
                    direction={column.getIsSorted() || undefined}
                    onSort={column.getToggleSortingHandler()}
                >
                    {t("label.last-seen")}
                </TableHeader>
            ),
            cell: (info) => {
                const device = info.row.original;
                return <StatusBadge compact online={device.online} lastSeen={device.last_seen} />;
            },
        }),

        columnHelper.accessor(
            (device) => {
                const ipv4 = device.ipv4 ?? "";
                const ipv6 = device.ipv6 ?? "";
                return `${ipv4} ${ipv6}`;
            },
            {
                id: "addresses",
                sortingFn: (rowA, rowB) => {
                    const a = ipv4ToInt(rowA.original.ipv4);
                    const b = ipv4ToInt(rowB.original.ipv4);
                    if (a !== null && b !== null) return a - b;
                    if (a !== null) return -1;
                    if (b !== null) return 1;
                    return (rowA.original.ipv6 ?? "").localeCompare(rowB.original.ipv6 ?? "");
                },
                header: ({ column }) => (
                    <TableHeader
                        sortable
                        direction={column.getIsSorted() || undefined}
                        onSort={column.getToggleSortingHandler()}
                    >
                        {t("label.addresses")}
                    </TableHeader>
                ),
                cell: (info) => {
                    const device = info.row.original;
                    return (
                        <div className="flex items-center gap-1">
                            <span>{device.ipv4 ?? "—"}</span>
                            <Dropdown>
                                <DropdownButton plain aria-label={tg("notice.copy")}>
                                    <ChevronDownIcon />
                                </DropdownButton>
                                <DropdownMenu anchor={"bottom end"}>
                                    {[
                                        device.ipv4,
                                        device.ipv6,
                                        device.dnsname,
                                        ...settingsContext.settings.headscale_domains.map(
                                            (domain) => `${device.dnsname}.${domain}`,
                                        ),
                                    ].map(
                                        (address) =>
                                            address && (
                                                <DropdownItem
                                                    key={address}
                                                    onClick={async () => {
                                                        await navigator.clipboard.writeText(address);
                                                        notify.success(tg("toast.copied-to-clipboard"));
                                                    }}
                                                >
                                                    <ClipboardDocumentIcon />
                                                    <DropdownLabel>{address}</DropdownLabel>
                                                </DropdownItem>
                                            ),
                                    )}
                                    {device.extra_records.length > 0 && (
                                        <>
                                            <DropdownDivider />
                                            {device.extra_records.map((record) => (
                                                <DropdownItem
                                                    key={record.uuid}
                                                    onClick={async () => {
                                                        await navigator.clipboard.writeText(record.from_dns);
                                                        notify.success(tg("toast.copied-to-clipboard"));
                                                    }}
                                                >
                                                    <ClipboardDocumentIcon />
                                                    <DropdownLabel className={"flex w-full justify-between gap-2"}>
                                                        <span className={"w-36 truncate"}>{record.from_dns}</span>
                                                        <Badge color={"blue"}>{t("label.extra-dns")}</Badge>
                                                    </DropdownLabel>
                                                </DropdownItem>
                                            ))}
                                        </>
                                    )}
                                </DropdownMenu>
                            </Dropdown>
                        </div>
                    );
                },
            },
        ),

        columnHelper.accessor((device) => device.expiry, {
            id: "key-expiry",
            enableGlobalFilter: false,
            header: ({ column }) => (
                <TableHeader
                    sortable
                    direction={column.getIsSorted() || undefined}
                    onSort={column.getToggleSortingHandler()}
                >
                    {t("label.key-expiry")}
                </TableHeader>
            ),
            cell: (info) => formatDate(info.getValue()),
            meta: {
                className: "max-lg:hidden",
            },
        }),

        columnHelper.accessor((device) => device.created_at, {
            id: "first-login",
            enableGlobalFilter: false,
            header: ({ column }) => (
                <TableHeader
                    sortable
                    direction={column.getIsSorted() || undefined}
                    onSort={column.getToggleSortingHandler()}
                >
                    {t("label.first-login")}
                </TableHeader>
            ),
            cell: (info) => formatDate(info.getValue()),
            meta: {
                className: "max-lg:hidden",
            },
        }),

        columnHelper.display({
            id: "actions",
            header: () => (
                <TableHeader className={"w-0"}>
                    <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                </TableHeader>
            ),
            size: 0,
            cell: ({ row }) => (
                <Dropdown>
                    <DropdownButton plain aria-label={tg("accessibility.actions")}>
                        <EllipsisVerticalIcon />
                    </DropdownButton>
                    <DropdownMenu anchor={"bottom end"}>
                        <DropdownSection>
                            <DropdownItem onClick={() => setOpenRenameDevice(row.original)}>
                                <PencilSquareIcon />
                                <DropdownLabel>{t("button.rename-device")}</DropdownLabel>
                            </DropdownItem>
                        </DropdownSection>

                        <DropdownDivider />

                        <DropdownSection>
                            <DropdownHeading>{t("heading.danger-zone")}</DropdownHeading>

                            <DropdownItem onClick={() => setOpenExpireKey(row.original)}>
                                <ClockIcon />
                                <DropdownLabel>{t("button.expire-key")}</DropdownLabel>
                            </DropdownItem>
                            <DropdownItem onClick={() => setOpenDeleteDevice(row.original)}>
                                <TrashIcon />
                                <DropdownLabel>{t("button.delete-device")}</DropdownLabel>
                            </DropdownItem>
                        </DropdownSection>
                    </DropdownMenu>
                </Dropdown>
            ),
            enableHiding: false,
        }),
    ];
}
